/**
 * I present SimpleHome — Vault's Simple view on the web (#476). One column, 760 px max:
 * the payday question when due, Safe to spend today, Log a spend, This month
 * (tick what's paid), money left in each bank, money in, and Show everything.
 * Budget (?s=budget) is the This month list on its own. It also hosts the
 * Simple log flow, the expense detail and, the replayable 4-step guide.
 *
 * Reads the month the Dashboard already loaded; every write goes through the
 * same endpoints the full view uses, then `refresh()` re-pulls the month.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../api.js';
import SimpleShell from './SimpleShell.jsx';
import SimpleLog from './SimpleLog.jsx';
import SimpleDetail from './SimpleDetail.jsx';
import SimpleGuide from './SimpleGuide.jsx';
import {
  BudgetViewSwitch, Icon, ordinal, rand, safeToSpend, showViewToast, useViewMode,
} from './viewMode.jsx';

const monthName = (ym) => {
  try { return new Date(`${ym}-01T00:00:00`).toLocaleDateString('en-GB', { month: 'long' }); } catch { return ym; }
};

export default function SimpleHome({ budget, summary, carry, banks = [], refresh }) {
  const [params, setParams] = useSearchParams();
  const budgetTab = params.get('s') === 'budget';
  const [screen, setScreen] = useState('home');   // home | log | detail
  const [detailId, setDetailId] = useState(null);
  const [settings, setSettings] = useState(null);
  const [pending, setPending] = useState({});     // optimistic ticks by item id
  const [answering, setAnswering] = useState(false);
  const [guideDone, setGuideDone] = useState(false);
  const [replayGuide, setReplayGuide] = useState(false);
  const { guideSeen, setMode, markGuideSeen } = useViewMode();

  useEffect(() => { api('/budget/settings/').then(setSettings).catch(() => {}); }, []);
  // A fresh month from the server clears the optimistic ticks.
  useEffect(() => { setPending({}); }, [budget]);
  useEffect(() => { window.scrollTo(0, 0); }, [screen, budgetTab]);
  // The 📖 Help button hides while the four-step guide is showing (home only).
  const guideShowing = (replayGuide || (!guideSeen && !guideDone)) && screen === 'home' && !budgetTab;
  useEffect(() => { window.dispatchEvent(new CustomEvent('vault-simple-guide', { detail: guideShowing })); }, [guideShowing]);

  const items = budget?.items || [];
  const expenses = useMemo(() => items.filter((i) => i.kind === 'expense' && i.source !== 'transfer'), [items]);
  // A move between your own banks (an income line with a from-bank) is not money in.
  const incomes = useMemo(() => items.filter((i) => i.kind === 'income' && i.source !== 'transfer' && !i.from_bank), [items]);
  const isDone = (i) => (i.id in pending ? pending[i.id] : !!i.completed);
  const nPaid = expenses.filter(isDone).length;
  const safe = safeToSpend(summary, settings?.cycle_start_day);
  const bankRows = (summary?.per_bank || []).filter((b) => b.has_actual);
  const liveBanks = useMemo(() => banks.filter((b) => !b.archived)
    .map((b) => ({ ...b, label: b.account_name ? `${b.name} · ${b.account_name}` : b.name })), [banks]);
  const bankName = (id) => (liveBanks.find((b) => b.id === id) || {}).label
    || ((summary?.per_bank || []).find((b) => b.bank === id) || {}).bank_name || '';

  /** Paid / received tick — the same complete endpoint the full view uses. No confirm: tapping again undoes it. */
  const tick = async (it) => {
    const next = !isDone(it);
    setPending((p) => ({ ...p, [it.id]: next }));
    try {
      const body = it.kind === 'income'
        ? (next ? { completed: true, actual: it.amount } : { completed: false })
        : { completed: next, settle: 'leftover' };
      await api(`/budget/items/${it.id}/complete/`, { method: 'POST', body });
      await refresh();
    } catch {
      setPending((p) => { const n = { ...p }; delete n[it.id]; return n; });
      showViewToast('Could not save — try again.');
    }
  };

  const showQuestion = !budgetTab && carry?.pending && !carry?.snoozed;
  const nextName = monthName(carry?.next_month || '');
  const answer = async (yes) => {
    setAnswering(true);
    try {
      if (yes) {
        await api(`/budget/months/${budget.id}/apply_carryover/`, { method: 'POST', body: { savings_amount: 0 } });
        showViewToast(`${nextName} has started. Your banks carried over.`);
      } else {
        await api(`/budget/months/${budget.id}/snooze_handoff/`, { method: 'POST', body: {} });
        showViewToast('We’ll ask again tomorrow.');
      }
      await refresh();
    } catch (e) {
      showViewToast(e?.data?.detail || 'Could not save — try again.');
    } finally { setAnswering(false); }
  };

  const showEverything = () => setMode('full', { toast: 'Showing everything. Switch back with “Simple view”.' });
  const onNav = (key) => {
    setScreen('home');
    if (key === 'budget') setParams({ s: 'budget' });
    else if (key === 'home') setParams({});
    else return false;
    return true;
  };

  const expenseRows = (
    <div className="sv-list">
      {expenses.map((e) => {
        const on = isDone(e);
        return (
          <div className="sv-row" key={e.id}>
            <button type="button" className="sv-row-main" onClick={() => { setDetailId(e.id); setScreen('detail'); }}>
              <span className={`sv-name${on ? ' is-done' : ''}`}>{e.title}</span>
              <span className="sv-amt">{rand(e.amount)}</span>
            </button>
            <button type="button" className="sv-tick" aria-pressed={on ? 'true' : 'false'}
                    aria-label={`${on ? 'Paid' : 'Not paid'}: ${e.title}`} onClick={() => tick(e)}>
              {Icon.check}
            </button>
          </div>
        );
      })}
    </div>
  );
  const monthBlock = (
    <section data-sv="month" style={{ display: 'flex', flexDirection: 'column' }}>
      <div style={{ marginBottom: '1rem' }}><BudgetViewSwitch /></div>
      <div className="sv-sechead">
        <h2 className="sv-h2">This month</h2>
        <span className="sv-count">{nPaid} of {expenses.length} paid</span>
      </div>
      <div className="sv-note" style={{ margin: '.25rem 0 .75rem' }}>Tap the circle when it’s paid.</div>
      {expenseRows}
    </section>
  );

  // Home / Budget are handled in place; Account leaves for its own route.
  const shell = (children, { showSwitch = false, active = budgetTab ? 'budget' : 'home' } = {}) => (
    <SimpleShell active={active} showSwitch={showSwitch} onNav={onNav}>
      {children}
    </SimpleShell>
  );

  if (screen === 'log') {
    return shell(
      <main className="sv-main">
        <SimpleLog budgetId={budget?.id} expenses={expenses} banks={liveBanks} bankName={bankName} perDay={safe.perDay}
                   refresh={refresh} onClose={() => setScreen('home')} />
      </main>,
    );
  }

  const detailItem = screen === 'detail' ? expenses.find((e) => e.id === detailId) : null;
  if (detailItem) {
    return shell(
      <main className="sv-main">
        <SimpleDetail item={detailItem} paid={isDone(detailItem)} onTick={() => tick(detailItem)}
                      refresh={refresh} onBack={() => setScreen('home')} />
      </main>,
    );
  }

  if (budgetTab) {
    return shell(<main className="sv-main sv-fade">{monthBlock}</main>, { showSwitch: true });
  }

  const guideOn = replayGuide || (!guideSeen && !guideDone);
  const closeGuide = () => { setReplayGuide(false); setGuideDone(true); markGuideSeen(); };

  return shell(
    <main className="sv-main">
      {showQuestion && (
        <section className="sv-question sv-fade" aria-labelledby="sv-payday-q">
          <div className="sv-question-title" id="sv-payday-q">Start {nextName}?</div>
          <div className="sv-question-body">Your banks carry over as they are.</div>
          <div className="sv-question-btns">
            <button type="button" className="sv-primary" disabled={answering} onClick={() => answer(true)}>Yes</button>
            <button type="button" className="sv-secondary" disabled={answering} onClick={() => answer(false)}>Not now</button>
          </div>
        </section>
      )}

      <section className="sv-safe" data-sv="safe">
        <div className="sv-safe-label">Safe to spend today</div>
        <div className="sv-safe-amt">{rand(safe.perDay)}</div>
        <div className="sv-safe-line">{rand(safe.left)} left until payday on the {ordinal(safe.payday)}</div>
      </section>

      <div data-sv="log">
        <button type="button" className="sv-primary" onClick={() => setScreen('log')}>
          {Icon.plus}Log a spend
        </button>
      </div>

      {!guideOn && <button type="button" className="sv-secondary" aria-label="Show Simple view guide"
        onClick={() => setReplayGuide(true)}>Show me how</button>}

      {monthBlock}

      {bankRows.length > 0 && (
        <section>
          <h2 className="sv-h2" style={{ marginBottom: '.75rem' }}>Money left in each bank</h2>
          <div className="sv-list">
            {bankRows.map((b) => (
              <div className="sv-bankrow" key={b.bank}>
                <span className="sv-name">{b.bank_name}</span>
                <span className="sv-amt">{rand(b.profit != null ? b.profit : b.remaining)}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {incomes.length > 0 && (
        <section>
          <h2 className="sv-h2" style={{ marginBottom: '.75rem' }}>Money in</h2>
          <div className="sv-list">
            {incomes.map((i) => {
              const on = isDone(i);
              return (
                <div className="sv-inrow" key={i.id}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="sv-name">{i.title}</div>
                    <div className="sv-inrow-meta">{on ? 'Received' : 'Not received yet'}</div>
                  </div>
                  <span className="sv-amt">{rand(i.amount)}</span>
                  <button type="button" className="sv-tick" aria-pressed={on ? 'true' : 'false'}
                          aria-label={`${on ? 'Received' : 'Not received'}: ${i.title}`} onClick={() => tick(i)}>
                    {Icon.check}
                  </button>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <section>
        <button type="button" className="sv-secondary" onClick={showEverything}>{Icon.grid}Show everything</button>
        <div className="sv-note" style={{ textAlign: 'center', marginTop: '.5rem' }}>Nothing is gone. Switch back any time.</div>
      </section>

      {guideOn && <SimpleGuide onClose={closeGuide} />}
    </main>,
    { showSwitch: true },
  );
}
